import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getClaimsNotificationsStatuses } from 'feature/claims/claimsSlice';
import {
	Collections,
	IFilterCheckEligibility,
	SubCollections,
	defaultSortingField,
	sortingDirections,
} from 'types/Form';
import {
	getClaimsStatusesThunk,
	getComplaintOutcomesThunk,
	getComplaintStatusesThunk,
	getComplaintTypeThunk,
	getFraudStatusesThunk,
	getReturnStatusesThunk,
} from './formThunk';

const initialState: IFilterCheckEligibility = {
	errorCode: 0,
	errorMessage: '',
	collection: Collections.NONE,
	subcollection: SubCollections.NONE,
	searchCheckElegibility: '',
	buttonSearchCheckElegibility: true,
	size: 10,
	search: false,
	inputCarrierClaimId: '',
	inputFiscalCode: '',
	inputMemberId: '',
	inputFirstName: '',
	inputLastName: '',
	inputDateOfBirth: null,
	dateOfService: null,
	isDateOfServiceValid: true,
	inputDateOfService: null,
	filterDateOfService: null,
	inputExternalClaimId: '',
	inputClaimFirstName: '',
	inputClaimLastName: '',
	inputClaimDate: '',
	inputClaimStatus: '',
	inputPaymentRunId: null,
	inputTotalAmount: null,
	sortingFields: '',
	sortingDirections: sortingDirections.DESC,
	claimsStatuses: [],
	notificationStatuses: [],
	claimReturnStatuses: [],
	fraudStatuses: [],
	complaintStatuses: [],
	complaintTypes: [],
	complaintOutcomes: [],
};

export const getClaimsStatuses = createAsyncThunk(
	'claims/getClaimsStatuses',
	getClaimsStatusesThunk,
);

export const getReturnStatuses = createAsyncThunk(
	'claim/getReturnStatuses',
	getReturnStatusesThunk,
);

export const getFraudStatuses = createAsyncThunk(
	'claim/getFraudStatuses',
	getFraudStatusesThunk,
);

export const getComplaintStatuses = createAsyncThunk(
	'claim/getComplaintStatuses',
	getComplaintStatusesThunk,
);

export const getComplaintTypes = createAsyncThunk(
	'claim/getComplaintTypes',
	getComplaintTypeThunk,
);

export const getComplaintOutcomes = createAsyncThunk(
	'claim/getComplaintOutcomes',
	getComplaintOutcomesThunk,
);

const formSlice = createSlice({
	name: 'form',
	initialState,
	reducers: {
		setCollection: (
			state: IFilterCheckEligibility,
			action: PayloadAction<Collections>,
		) => {
			state.sortingFields = defaultSortingField[action.payload];
			state.collection = action.payload;
		},
		setSubCollection: (
			state: IFilterCheckEligibility,
			action: PayloadAction<SubCollections>,
		) => {
			state.subcollection = action.payload;
		},
		handleSearchValuesForm: (state: IFilterCheckEligibility) => {
			state = { ...state };
		},
		handleSearchValueChange: (
			state: IFilterCheckEligibility | any,
			action: PayloadAction<React.ChangeEvent<HTMLInputElement>>,
		) => {
			const { name, value } = action.payload.target;
			state[name] = value;
		},
		handleChange: (state: any, action: any) => {
			state[action.payload.name] = action.payload.value;
		},
		setSearch: (
			state: IFilterCheckEligibility,
			{ payload }: PayloadAction<boolean>,
		) => {
			state.search = payload;
		},
		handleChangeClaimStatus: (
			state: IFilterCheckEligibility,
			action: PayloadAction<string>,
		) => {
			if (action.payload === '') {
				state['inputClaimStatus'] = '';
			} else {
				state['inputClaimStatus'] = action.payload;
			}
		},
		setSortingValues: (
			state: any,
			action: PayloadAction<{ name: string; value: string }>,
		) => {
			const { name, value } = action.payload;
			state.sortingFields = name;
			state.sortingDirections = value;
		},
		checkSearch: (state: IFilterCheckEligibility) => {
			if (
				state.searchCheckElegibility === 'inputByFiscalCode' &&
				!!state.inputFiscalCode
			) {
				state.buttonSearchCheckElegibility = false;
			}
		},
		resetInputDateOfBirth: state => {
			state.inputDateOfBirth = null;
		},
		resetInputMemberId: state => {
			state.inputMemberId = '';
		},
		resetDateOfService: state => {
			state.dateOfService = null;
		},
		resetForm: state => ({
			...initialState,
			claimsStatuses: state.claimsStatuses,
			notificationStatuses: state.notificationStatuses,
			claimReturnStatuses: state.claimReturnStatuses,
			fraudStatuses: state.fraudStatuses,
			complaintStatuses: state.complaintStatuses,
			complaintTypes: state.complaintTypes,
			complaintOutcomes: state.complaintOutcomes,
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getClaimsStatuses.fulfilled, (state, { payload }) => {
				state.claimsStatuses = [...payload].sort((a, b) => (a.id > b.id ? 1 : -1));
			})
			.addCase(getClaimsStatuses.rejected, state => {
				state.claimsStatuses = [];
			})
			.addCase(getClaimsNotificationsStatuses.fulfilled, (state, { payload }) => {
				state.notificationStatuses = payload;
			})
			.addCase(getClaimsNotificationsStatuses.rejected, state => {
				state.notificationStatuses = [];
			})
			.addCase(getReturnStatuses.fulfilled, (state, { payload }) => {
				state.claimReturnStatuses = payload;
			})
			.addCase(getFraudStatuses.fulfilled, (state, { payload }) => {
				state.fraudStatuses = payload;
			})
			.addCase(getComplaintStatuses.fulfilled, (state, { payload }) => {
				state.complaintStatuses = payload;
			})
			.addCase(getComplaintTypes.fulfilled, (state, { payload }) => {
				state.complaintTypes = payload;
			})
			.addCase(getComplaintOutcomes.fulfilled, (state, { payload }) => {
				state.complaintOutcomes = payload;
			});
	},
});

export const {
	setCollection,
	setSubCollection,
	handleSearchValueChange,
	resetForm,
	checkSearch,
	handleChange,
	handleChangeClaimStatus,
	setSearch,
	setSortingValues,
	resetInputDateOfBirth,
	resetInputMemberId,
	resetDateOfService,
} = formSlice.actions;
export default formSlice.reducer;
